import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { ApPayment, ApPaymentInsert, ApPaymentPostConfirm, ApPaymentSearchCommand } from '../models/ap-payment/ap-payment';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class ApPaymentProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/ap/payments';

  public getById(id: string): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.get<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}`);
  }

  public createDraft(data: ApPaymentInsert): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.post<ApiResponse<ApPayment>>(this.baseUrl, data);
  }

  public updateDraft(id: string, data: ApPaymentInsert): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.put<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}`, data);
  }

  public post(id: string, confirm: ApPaymentPostConfirm): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.post<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}/post`, confirm);
  }

  /**
   * Puts a posted payment back to draft so it can be corrected and posted again.
   *
   * <p>Undoes all six things posting did — reverses the journal entry, restores the invoices and the
   * advances it drew down, voids the bank movement and its charges, and pushes the purchase orders back
   * off Paid. Refused when the bank movement is already reconciled against a statement line: the money
   * demonstrably moved, and that is a return rather than a correction.</p>
   */
  public unpost(id: string): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.post<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}/unpost`, {});
  }

  public uploadReceipt(id: string, file: File): Observable<ApiResponse<ApPayment>> {
    const form = new FormData();
    form.append('file', file);
    return this.httpClient.post<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}/receipt`, form);
  }

  public search(
    command: ApPaymentSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<ApPayment>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ApPayment>>>(`${this.baseUrl}/search`, command, { params });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
